import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useStudent } from "../contexts/StudentContext";
import { getCatalog } from "../services/contentService";
import { syncNotifications } from "../services/notificationService";
import { markOpenedFromNotification } from "../services/analytics";
import type { Catalog } from "../content/types";

/**
 * Replanifie les rappels locaux (natif uniquement) au démarrage, à chaque retour au premier
 * plan et après chaque activité pertinente : préférences, série (jour d'activité) et échéances
 * de révision. Une « signature » évite de solliciter le plugin quand rien n'a changé.
 */
export function useNotificationSync(): void {
  const { user, profile, stats, progress, isParent } = useStudent();
  const native = Capacitor.isNativePlatform();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [resumeTick, setResumeTick] = useState(0);

  useEffect(() => {
    if (!native) return;
    getCatalog().then(setCatalog).catch(() => undefined);
  }, [native]);

  // Retour au premier plan (nouveau jour possible) + ouverture depuis un rappel (analytics).
  useEffect(() => {
    if (!native) return;
    const removers: Array<() => void> = [];
    import("@capacitor/app")
      .then(({ App: CapApp }) =>
        CapApp.addListener("appStateChange", ({ isActive }) => isActive && setResumeTick((t) => t + 1)).then((h) => removers.push(() => h.remove()))
      )
      .catch(() => undefined);
    import("@capacitor/local-notifications")
      .then(({ LocalNotifications }) =>
        LocalNotifications.addListener("localNotificationActionPerformed", () => markOpenedFromNotification()).then((h) => removers.push(() => h.remove()))
      )
      .catch(() => undefined);
    return () => removers.forEach((r) => r());
  }, [native]);

  const prefs = profile?.notifications;
  const dueDates = Object.values(progress)
    .map((p) => p.review?.nextDueAt || 0)
    .filter(Boolean)
    .sort()
    .join(",");
  const signature = [
    user?.uid || "",
    isParent ? "parent" : "student",
    prefs ? `${prefs.enabled}:${prefs.hour}:${prefs.minute}` : "off",
    stats.streak.current,
    stats.streak.lastActiveDay || "",
    dueDates,
    catalog ? catalog.version : "nocat",
    resumeTick,
  ].join("|");

  useEffect(() => {
    if (!native || !user || !profile) return;
    syncNotifications({ profile, stats, progress, catalog }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
